import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, MousePointer, RefreshCw, Calendar, 
  Filter, Download, Eye, Zap
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HeatmapViewer = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [pagePreview, setPagePreview] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchHeatmapData();
    fetchPagePreview();
  }, [slug, days]);

  const fetchHeatmapData = async () => {
    try {
      const res = await axios.get(`${API}/admin/heatmap/${slug}?days=${days}`);
      setData(res.data);
      setTimeout(() => drawHeatmap(res.data.clicks), 100);
    } catch (err) {
      toast.error("Failed to load heatmap data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPagePreview = async () => {
    try {
      const res = await axios.get(`${API}/public/event-page/${slug}`);
      setPagePreview(res.data);
    } catch (err) {
      console.log("Could not load page preview");
    }
  };

  const drawHeatmap = (clicks) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !clicks?.length) return;

    const ctx = canvas.getContext("2d");
    const width = container.offsetWidth;
    const height = 800;
    
    canvas.width = width;
    canvas.height = height;
    
    ctx.clearRect(0, 0, width, height);

    // Draw heatmap points
    clicks.forEach(click => {
      const x = (click.x_percent / 100) * width;
      const y = (click.y_percent / 100) * height;
      
      // Create radial gradient for each point
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, 30);
      gradient.addColorStop(0, "rgba(255, 0, 0, 0.5)");
      gradient.addColorStop(0.5, "rgba(255, 165, 0, 0.3)");
      gradient.addColorStop(1, "rgba(255, 255, 0, 0)");
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, 30, 0, Math.PI * 2);
      ctx.fill();
    });
  };

  const elementColors = {
    button: "#D4AF37",
    link: "#3B82F6",
    ticket: "#10B981",
    faq: "#8B5CF6",
    other: "#6B7280"
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin/conversions" 
              className="p-2 text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[#F5F5F0] flex items-center gap-2">
                <MousePointer className="w-6 h-6 text-[#D4AF37]" />
                Heatmap: {slug}
              </h1>
              <p className="text-[#A1A1AA]">Click patterns visualization</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="px-4 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={fetchHeatmapData}
              className="p-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors rounded"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent"></div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Heatmap Visualization */}
            <div className="lg:col-span-2 bg-[#121212] border border-[#27272A] rounded-lg overflow-hidden">
              <div className="p-4 border-b border-[#27272A] flex items-center justify-between">
                <h2 className="font-semibold text-[#F5F5F0]">Click Heatmap</h2>
                <span className="text-sm text-[#A1A1AA]">{data?.total_clicks || 0} total clicks</span>
              </div>
              <div ref={containerRef} className="relative" style={{ height: "800px", background: "#fafafa" }}>
                {/* Page preview background */}
                <iframe
                  src={`/${slug}`}
                  className="absolute inset-0 w-full h-full pointer-events-none opacity-50"
                  title="Page preview"
                />
                {/* Heatmap overlay */}
                <canvas
                  ref={canvasRef}
                  className="absolute inset-0 w-full h-full pointer-events-none"
                />
              </div>
            </div>

            {/* Stats Sidebar */}
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-[#121212] border border-[#27272A] rounded-lg p-6">
                <h3 className="font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#D4AF37]" />
                  Click Summary
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-[#A1A1AA]">Total Clicks</span>
                    <span className="text-2xl font-bold text-[#F5F5F0]">{data?.total_clicks || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#A1A1AA]">Period</span>
                    <span className="text-[#F5F5F0]">{days} days</span>
                  </div>
                </div>
              </div>

              {/* Element Breakdown */}
              <div className="bg-[#121212] border border-[#27272A] rounded-lg p-6">
                <h3 className="font-semibold text-[#F5F5F0] mb-4">Clicks by Element</h3>
                <div className="space-y-3">
                  {data?.element_summary && Object.entries(data.element_summary)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 10)
                    .map(([element, count]) => (
                      <div key={element} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: elementColors[element] || elementColors.other }}
                          />
                          <span className="text-[#A1A1AA] text-sm truncate max-w-[150px]">{element}</span>
                        </div>
                        <span className="text-[#F5F5F0] font-medium">{count}</span>
                      </div>
                    ))}
                  {(!data?.element_summary || Object.keys(data.element_summary).length === 0) && (
                    <p className="text-[#a1a1aa] text-sm">No click data yet</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="bg-[#121212] border border-[#27272A] rounded-lg p-6">
                <h3 className="font-semibold text-[#F5F5F0] mb-4">Actions</h3>
                <div className="space-y-2">
                  <Link
                    to={`/${slug}`}
                    target="_blank"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors rounded"
                  >
                    <Eye className="w-4 h-4" />
                    View Live Page
                  </Link>
                  <Link
                    to={`/admin/landing-pages`}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-[#27272A] text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors rounded"
                  >
                    Edit Page
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default HeatmapViewer;
